import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CheckValid from "../../Common/CheckValid";
import {
  WARRANTY_EXCEPTIONS_URL,
  WARRANTY_SUBSCRIPTIONS_URL,
  WARRANTY_SUBSCRIPTION_URL,
} from "../../Shared/constant";
import { WARRANTY_PERMISSION, hasWarrantyPermission } from "../common/warrantyAccess";
import { Loading, EmptyState, ErrorState } from "../common/WarrantyStates";
import WarrantyPage from "../common/WarrantyPage";
import { usePagedList } from "../common/usePagedList";
import { getRefundCheck, listRecentRefunds, refundSubscription } from "../common/warrantyApi";
import {
  clearPending,
  pendingRequestId,
  recordAnswer,
  unresolvedFor,
  usePendingRequests,
} from "../common/pendingRequests";
import { useWarrantyNav } from "../common/useWarrantyNav";
import { formatDate, formatDateTime, formatMinor } from "../common/format";
import {
  REFUND_REQUEST_REASONS,
  refundCheckText,
  refundReasonLabel,
  requestStateLabel,
  subscriptionStatusLabel,
} from "../common/opsLabels";
import "../warranty.css";

// The id format check lives in CheckValid (warranty_uuid); this only asks it a yes or no.
const isUuid = (text) => {
  let message = "";
  CheckValid(text.trim(), {
    type: "warranty_uuid",
    error: (m) => {
      message = m;
    },
  });
  return message === "";
};
const shortId = (id) => (typeof id === "string" ? `${id.slice(0, 8)}...${id.slice(-4)}` : "-");

const refundKey = (subscriptionId) => `refund:${subscriptionId}`;

const RefundDialog = ({ check, accessToken, onClose, onDone }) => {
  const key = refundKey(check.subscriptionId);
  // The id and reason of a refund that may already have gone through are kept outside this dialog
  // (pendingRequests), so cancelling and reopening it cannot send the refund under a new id.
  const held = unresolvedFor(key);
  // No reason is chosen at the start: the person picks the true one.
  const [reasonCode, setReasonCode] = useState(held ? held.reason : null);
  const [inFlight, setInFlight] = useState(false);
  const [failure, setFailure] = useState("");
  const [locked, setLocked] = useState(held !== null);
  const amount = formatMinor(check.refundableMinor, check.currency);

  const confirm = async () => {
    if (inFlight || reasonCode === null) {
      return;
    }
    setInFlight(true);
    setFailure("");
    const result = await refundSubscription(
      accessToken,
      check.subscriptionId,
      reasonCode,
      pendingRequestId(key, `refund:${check.subscriptionId}:${reasonCode}`)
    );
    const unsure = recordAnswer(key, result, reasonCode);
    setInFlight(false);
    setLocked(unsure);
    if (result.ok) {
      onDone(result.data);
    } else {
      setFailure(result.message);
      onDone(null, result);
    }
  };

  return (
    <Modal
      show
      onHide={inFlight ? () => {} : onClose}
      backdrop={inFlight ? "static" : true}
      keyboard={!inFlight}
      className="commonModal"
      centered
    >
      <Modal.Header closeButton={!inFlight}>
        <Modal.Title>Refund this subscription</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="warranty-dialog-hint">
          The rider will get {amount} back to the way they paid, the plan ends, and benefits not yet
          used are cancelled. This cannot be undone.
        </p>
        <fieldset className="formloginset warranty-reason-codes">
          <legend>Why is this refund being made?</legend>
          {REFUND_REQUEST_REASONS.map((code) => (
            <Form.Check
              key={code}
              type="radio"
              id={`warranty-refund-reason-${code}`}
              name="warrantyRefundReason"
              label={refundReasonLabel(code)}
              checked={reasonCode === code}
              onChange={() => setReasonCode(code)}
              disabled={inFlight || locked}
            />
          ))}
        </fieldset>
        {locked && !failure && (
          <div className="warranty-banner is-warning" role="status">
            <span>
              We could not confirm the earlier refund. It may have gone through. Confirming again sends the
              same request, so the reason cannot be changed.
            </span>
          </div>
        )}
        {failure && (
          <div className="warranty-dialog-error" role="alert">
            {failure}
            {locked && " The refund may have gone through; confirming again repeats the same request safely."}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={inFlight}>
          Cancel
        </Button>
        <Button className="addbtncmn" onClick={confirm} disabled={inFlight || reasonCode === null}>
          {inFlight ? "Working" : `Refund ${amount}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const Refunds = () => {
  const [params, setParams] = useSearchParams();
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const allowed = hasWarrantyPermission(selfInfo, WARRANTY_PERMISSION.OPS);
  useWarrantyNav(allowed);

  const fromUrl = (params.get("subscription") || "").trim();
  const [value, setValue] = useState(isUuid(fromUrl) ? fromUrl : "");
  const [fieldError, setFieldError] = useState("");
  const [check, setCheck] = useState({ status: "idle", data: null, message: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [done, setDone] = useState(null);
  const [recheckError, setRecheckError] = useState("");
  usePendingRequests();
  const seq = useRef(0);
  const mounted = useRef(true);
  const checkedId = useRef("");

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const fetchRecent = useCallback((cursor) => listRecentRefunds(accessToken, cursor), [accessToken]);
  const recent = usePagedList(fetchRecent, allowed);

  const runCheck = useCallback(
    async (id, quiet = false) => {
      const mine = ++seq.current;
      setRecheckError("");
      if (!quiet) {
        checkedId.current = id;
        setCheck({ status: "loading", data: null, message: "" });
        setDone(null);
      }
      const result = await getRefundCheck(accessToken, id);
      if (!mounted.current || mine !== seq.current) {
        return;
      }
      if (result.ok) {
        setCheck({ status: "ready", data: result.data, message: "" });
        // The subscription now reads as refunded: whatever the unclear answer was, it is settled.
        if (result.data.status === "refunded") {
          clearPending(refundKey(result.data.subscriptionId));
        }
      } else if (!quiet) {
        setCheck({ status: "error", data: null, message: result.message });
      } else {
        setRecheckError(result.message);
      }
    },
    [accessToken]
  );

  // A link from the subscription screen carries the id (a UUID, never personal data).
  useEffect(() => {
    if (allowed && isUuid(fromUrl) && checkedId.current !== fromUrl) {
      runCheck(fromUrl);
    }
  }, [allowed, fromUrl, runCheck]);

  const validate = (text) => {
    let message = "";
    CheckValid(text.trim(), {
      type: "warranty_uuid",
      error: (m) => {
        message = m;
      },
    });
    setFieldError(message);
    return message === "";
  };

  const submit = () => {
    if (check.status === "loading" || !validate(value)) {
      return;
    }
    setParams({ subscription: value.trim() }, { replace: true });
    runCheck(value.trim());
  };

  const afterRefund = (data, refusal) => {
    if (data) {
      setDialogOpen(false);
      setDone(data);
      toast.success("The refund was recorded.");
      runCheck(data.subscriptionId, true);
      recent.reload(true);
    } else if (refusal && refusal.httpStatus >= 400 && refusal.httpStatus < 500) {
      // The checks moved under the person: read them again, keep the dialog's message.
      runCheck(check.data.subscriptionId, true);
    } else if (refusal && unresolvedFor(refundKey(check.data.subscriptionId))) {
      // No clear answer: the refund may have gone through. Read the checks and the recent refunds.
      runCheck(check.data.subscriptionId, true);
      recent.reload(true);
    }
  };

  const data = check.data;
  const unconfirmed = data ? unresolvedFor(refundKey(data.subscriptionId)) !== null : false;

  return (
    <WarrantyPage
      title="Refunds"
      sub="Check a subscription for a cooling-off refund, make it, and see the refunds made recently."
      allowed={allowed}
      deniedTitle="You do not have access to refunds"
      deniedText="Ask an admin to give you the Warranty Operations permission."
    >
      <form
        className="formloginset warranty-lookup"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
        autoComplete="off"
      >
        <Form.Group controlId="warrantyRefundSubscription" className="warranty-lookup-value">
          <Form.Label>Subscription id</Form.Label>
          <Form.Control
            type="text"
            value={value}
            maxLength={36}
            placeholder="0199a1b2-0000-7000-8000-000000000001"
            onChange={(event) => {
              setValue(event.target.value);
              if (fieldError) {
                validate(event.target.value);
              }
            }}
            autoComplete="off"
            spellCheck={false}
          />
        </Form.Group>
        <Button className="Addbtn warranty-lookup-btn" type="submit" disabled={check.status === "loading"}>
          {check.status === "loading" ? "Checking" : "Check"}
        </Button>
        <Link className="warranty-inline-btn warranty-lookup-btn" to={WARRANTY_SUBSCRIPTIONS_URL}>
          Find a subscription
        </Link>
      </form>
      <div className="warranty-lookup-msg" aria-live="polite">
        {fieldError && (
          <span className="danger-colorset" role="alert">
            {fieldError}
          </span>
        )}
      </div>

      {check.status === "loading" && <Loading label="Checking the subscription" />}
      {check.status === "error" && (
        <ErrorState message={check.message} onRetry={() => runCheck(value.trim())} />
      )}
      {done && (
        <div className="warranty-banner is-info" role="status">
          <span>
            Refund recorded: {formatMinor(done.totalMinor, done.currency)} on {formatDateTime(done.refundedAt)}
            . The money goes back through the provider.
          </span>
        </div>
      )}
      {recheckError && (
        <div className="warranty-banner is-warning" role="alert">
          <span>The checks could not be read again: {recheckError}</span>
        </div>
      )}
      {data && check.status === "ready" && (
        <section className="warranty-panel" aria-label="Refund check">
          <h5>
            Refund check{" "}
            <span className="warranty-muted">
              subscription is {subscriptionStatusLabel(data.status).toLowerCase()}
              {data.windowEndsOn ? `, cooling-off window ends ${formatDate(data.windowEndsOn)}` : ""}
            </span>
          </h5>
          <ul className="warranty-checks-list">
            {data.checks.map((item) => (
              <li key={item.name} className={item.ok ? "is-ok" : "is-bad"}>
                <span className="warranty-check-mark" aria-hidden="true">
                  {item.ok ? "✓" : "✗"}
                </span>
                <span className="warranty-sr-only">{item.ok ? "Passed: " : "Failed: "}</span>
                {refundCheckText(item, data.windowEndsOn)}
              </li>
            ))}
          </ul>
          <h6 className="warranty-subhead-plain">Money that would go back</h6>
          {data.payments.length === 0 ? (
            <div className="warranty-muted">No payment would be refunded.</div>
          ) : (
            <table className="warranty-preview-table warranty-refund-table">
              <thead>
                <tr>
                  <th scope="col">Payment</th>
                  <th scope="col" className="warranty-num">
                    Paid
                  </th>
                  <th scope="col" className="warranty-num">
                    Goes back
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.payments.map((payment) => (
                  <tr key={payment.paymentId}>
                    <th scope="row">{shortId(payment.paymentId)}</th>
                    <td className="warranty-num">{formatMinor(payment.amountMinor, data.currency)}</td>
                    <td className="warranty-num">{formatMinor(payment.refundMinor, data.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="warranty-refund-total">
            Total refund now: <strong>{formatMinor(data.refundableMinor, data.currency)}</strong>
          </p>
          <div className="warranty-card-actions">
            <button
              type="button"
              className="warranty-inline-btn is-danger"
              onClick={() => setDialogOpen(true)}
              disabled={
                !data.eligible || unconfirmed || (done !== null && done.subscriptionId === data.subscriptionId)
              }
              title={data.eligible ? "" : "Not eligible: see the checks above"}
            >
              Refund
            </button>
            <Link
              className="warranty-inline-btn"
              to={`${WARRANTY_SUBSCRIPTION_URL}/${encodeURIComponent(data.subscriptionId)}`}
            >
              Open subscription
            </Link>
            {!data.eligible && (
              <span className="warranty-muted">The refund is off because a check above failed.</span>
            )}
          </div>
          {unconfirmed && (
            <div className="warranty-banner is-warning" role="alert">
              <span>We could not confirm the refund. Check the recent refunds before trying again.</span>
              <div className="warranty-banner-actions">
                <button
                  type="button"
                  className="warranty-inline-btn"
                  onClick={() => {
                    runCheck(data.subscriptionId, true);
                    recent.reload(true);
                  }}
                >
                  Check again
                </button>
                <button type="button" className="warranty-inline-btn" onClick={() => setDialogOpen(true)}>
                  Send the same refund again
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      <section className="warranty-panel" aria-label="Recent refunds">
        <h5>Recent refunds</h5>
        {recent.status === "loading" && <Loading label="Loading recent refunds" />}
        {recent.status === "error" && (
          <ErrorState message={recent.message} onRetry={() => recent.reload(false)} />
        )}
        {recent.status === "ready" && recent.items.length === 0 && (
          <EmptyState title="No refunds yet" />
        )}
        {recent.status === "ready" && recent.items.length > 0 && (
          <>
            <div className="tableView warranty-ops-table">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Asked</th>
                    <th scope="col">Why</th>
                    <th scope="col" className="warranty-num">
                      Amount
                    </th>
                    <th scope="col">State</th>
                    <th scope="col">Subscription</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.items.map((item) => (
                    <tr key={`${item.paymentId}-${item.requestedAt}`}>
                      <td>{formatDateTime(item.requestedAt)}</td>
                      <td>{refundReasonLabel(item.reason)}</td>
                      <td className="warranty-num">{formatMinor(item.amountMinor, item.currency)}</td>
                      <td>
                        <span className={`warranty-badge is-refund-${item.state}`}>
                          {requestStateLabel(item.state)}
                        </span>
                        {item.state === "refunded" && item.refundedAt && (
                          <div className="warranty-muted">{formatDate(item.refundedAt)}</div>
                        )}
                        {item.state === "failed" && (
                          <div>
                            <Link to={`${WARRANTY_EXCEPTIONS_URL}/refund_failed`}>Retry it from Health</Link>
                          </div>
                        )}
                      </td>
                      <td>
                        <Link to={`${WARRANTY_SUBSCRIPTION_URL}/${encodeURIComponent(item.subscriptionId)}`}>
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="warranty-more">
              {recent.moreError && (
                <span className="warranty-field-msg is-error" role="alert">
                  {recent.moreError}
                </span>
              )}
              {recent.nextCursor && (
                <Button className="Addbtn" onClick={recent.loadMore} disabled={recent.loadingMore}>
                  {recent.loadingMore ? "Loading" : "Load more"}
                </Button>
              )}
            </div>
          </>
        )}
      </section>

      {dialogOpen && data && (
        <RefundDialog
          key={data.subscriptionId}
          check={data}
          accessToken={accessToken}
          onClose={() => {
            setDialogOpen(false);
            if (unresolvedFor(refundKey(data.subscriptionId))) {
              runCheck(data.subscriptionId, true);
              recent.reload(true);
            }
          }}
          onDone={afterRefund}
        />
      )}
    </WarrantyPage>
  );
};

export default Refunds;
